import { use, useEffect, useRef, useState } from "react";
import Sidebar from "../../customComponents/Sidebar/Sidebar";
import {
  checkin,
  getEventLogs,
  getUncheckedParticipantsLogs,
} from "./apiRoutes/events";
import {
  Loader2,
  TriangleAlert,
  CircleCheckBig,
  X,
  CheckCircle,
} from "lucide-react";
import { ClipLoader } from "react-spinners";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  Box,
  Container,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
  TextField,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputLabel,
  Button,
} from "@mui/material";
import { MetricChart, MetricStat } from "./pageComponents/ChartComponents";
import { getEventMeta } from "./apiRoutes/eventMeta";

const query = new URLSearchParams(window.location.search);

const baseColors = [
  "rgba(255, 99, 132, 0.6)",
  "rgba(54, 162, 235, 0.6)",
  "rgba(255, 206, 86, 0.6)",
  "rgba(75, 192, 192, 0.6)",
  "rgba(153, 102, 255, 0.6)",
  "rgba(255, 159, 64, 0.6)",
];

function EventMetrics() {
  const isEventLogsSet = useRef(false);
  const isEventMetaSet = useRef(false);
  const [originalEventLogs, setOriginalEventLogs] = useState([]);
  const [originalEventMeta, setOriginalEventMeta] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState({
    name: "",
    id: "-1",
  });
  const [events, setEvents] = useState([]);
  const [ageGroups, setAgeGroups] = useState({});
  const [demographicCount, setDemographicCount] = useState({
    adult_count: 0,
    child_count: 0,
  });
  const [eventLogDemographicCount, setEventLogDemographicCount] = useState({
    adult_count: 0,
    child_count: 0,
  });
  const [eventMetaDemographicCount, setEventMetaDemographicCount] = useState({
    adult_count: 0,
    child_count: 0,
  });
  const [accountRecency, setAccountRecency] = useState({
    new_accounts: 0,
    old_accounts: 0,
  });

  const isAdmin = tmtAdminPortal.user_roles.includes("administrator");

  if (!isAdmin) {
    window.location.href = "portal-login";
    return null;
  }

  useEffect(() => {
    initializeEventLogs();
    initalizeEventMeta();
  }, [selectedEvent]);

  useEffect(() => {
    initializeEventOptions();
  }, [originalEventLogs]);

  const initializeEventOptions = () => {
    const eventSet = new Set();
    const newEventOptions = [];

    originalEventLogs.forEach((val) => {
      const eventId = val.event_id;
      if (!eventSet.has(eventId)) {
        eventSet.add(eventId);
        newEventOptions.push({
          name: val.event_title,
          id: val.event_id,
        });
      }
    });

    if (newEventOptions.length > 0) {
      setSelectedEvent(newEventOptions[0]);
    }
    setEvents(newEventOptions);
  };

  const initializeEventLogs = async () => {
    const eventLogs =
      originalEventLogs.length > 0 ? originalEventLogs : await getEventLogs();
    const eventSet = new Set();
    const newEventOptions = [];
    const newAgeGroups = [];
    const newAccountRecencies = {
      new_accounts: 0,
      old_accounts: 0,
    };
    const newEventLogDemographics = {
      adult_count: 0,
      child_count: 0,
    };
    const newEventMetaDemographic = {
      adult_count: 0,
      child_count: 0,
    };

    eventLogs
      .filter((val) => val.event_title === selectedEvent.name)
      .forEach((val) => {
        const eventId = val.event_id;
        const age = val.user.age.toString();

        if (parseInt(val.is_new_account)) {
          newAccountRecencies.new_accounts += 1;
        } else {
          newAccountRecencies.old_accounts += 1;
        }

        if (age === "") {
          newEventMetaDemographic.adult_count += 1;
          setEventMetaDemographicCount(newEventMetaDemographic);
          return;
        }

        if (age >= 18) {
          newEventLogDemographics.adult_count += 1;
        } else {
          newEventLogDemographics.child_count += 1;
        }

        if (!eventSet.has(eventId)) {
          eventSet.add(eventId);
          newEventOptions.push({
            name: val.event_title,
            id: val.event_id,
          });
        }

        if (newAgeGroups[age]) {
          newAgeGroups[age] += 1;
        } else {
          newAgeGroups[age] = 1;
        }
      });

    if (!isEventMetaSet.current) {
      isEventLogsSet.current = true;
      setOriginalEventLogs(eventLogs);
    }

    setEventLogDemographicCount(newEventLogDemographics);
    setAccountRecency(newAccountRecencies);
    setAgeGroups(newAgeGroups);
  };

  const initalizeEventMeta = async () => {
    const newEventMeta =
      originalEventMeta.length > 0 ? originalEventMeta : await getEventMeta();
    const acceptedMetaKeys = ["adult_count", "child_count"];
    const newDemographicCount = { adult_count: 0, child_count: 0 };

    newEventMeta
      .filter((val) => val.event_id === selectedEvent.id)
      .forEach((val) => {
        const metaKey = val.meta_key;
        const count = val.meta_value;

        if (!acceptedMetaKeys.includes(metaKey)) {
          return;
        }

        newDemographicCount[metaKey] += parseInt(count);
      });

    if (!isEventMetaSet.current) {
      isEventMetaSet.current = true;
      setOriginalEventMeta(newEventMeta);
    }

    setDemographicCount(newDemographicCount);
  };

  const generateUniqueColors = (count) => {
    return Array.from({ length: count }, (_, i) => {
      const hue = Math.floor((360 / count) * i); // even spacing
      return `hsla(${hue}, 70%, 60%, 0.6)`; // nice bright colors
    });
  };

  const ageGroupLabels = ["Adult", "Child", ...Object.keys(ageGroups)];
  const ageGroupData = [
    demographicCount.adult_count + eventMetaDemographicCount.adult_count,
    demographicCount.child_count + eventMetaDemographicCount.child_count,
    ...Object.keys(ageGroups).map((key) => ageGroups[key]),
  ];

  const ageData = {
    labels: ageGroupLabels,
    datasets: [
      {
        label: "Demographic",
        data: ageGroupData,
        backgroundColor: generateUniqueColors(ageGroupLabels.length),
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  };

  const ageOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Demographic",
        align: "start",
        color: "#000000", // 🔹 change title color
        font: {
          size: 18, // 🔹 increase title font size
          weight: "bold",
          family: "Arial", // optional
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.parsed;
            const dataset = context.chart.data.datasets[context.datasetIndex];
            const total = dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 1000) / 10; // nearest tenth
            return `${label}: ${percentage}%`;
          },
        },
      },
    },
  };

  return (
    <div className="admin-portal-container">
      <Sidebar selectedIndex={0} />
      <Stack
        direction="column"
        justifyContent="space-between"
        alignItems="start"
        sx={{
          pt: 2,
          width: "100%",
          px: { sm: 2, lg: 4 },
        }}
        gap={4}
      >
        <Box sx={{ minWidth: 250 }}>
          <Autocomplete
            disablePortal
            size="small"
            value={
              selectedEvent.name === ""
                ? { name: "Select Event" }
                : events.find(
                    (m) =>
                      m.name.toLowerCase() === selectedEvent.name.toLowerCase()
                  ) || null
            }
            onChange={(event, newValue) => {
              if (newValue) {
                setSelectedEvent(
                  newValue.name === "Select Event"
                    ? { name: "", id: "" }
                    : newValue
                );
              }
            }}
            options={[{ name: "Select Event" }, ...events]}
            getOptionLabel={(option) => option.name || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Event"
                variant="outlined"
                sx={{ height: "1rem" }}
              />
            )}
          />
        </Box>
        <div className="event-metrics-container">
          <Box
            sx={{
              // height: 400,
              // gridColumn: "span 2",
              gridRow: "span 2",
            }}
          >
            <MetricChart data={ageData} options={ageOptions} type={"pie"} />
          </Box>

          <Box sx={{ height: 200, flex: 1 }}>
            <MetricStat
              title={"Adult Count"}
              value={
                demographicCount.adult_count +
                eventLogDemographicCount.adult_count +
                eventMetaDemographicCount.adult_count
              }
            />
          </Box>
          <Box sx={{ height: 200, flex: 1 }}>
            <MetricStat
              title={"Child Count"}
              value={
                demographicCount.child_count +
                eventLogDemographicCount.child_count +
                eventMetaDemographicCount.child_count
              }
            />
          </Box>
          <Box sx={{ height: 200, flex: 1 }}>
            <MetricStat
              title={"New Accounts"}
              value={accountRecency.new_accounts}
            />
          </Box>
          <Box sx={{ height: 200, flex: 1 }}>
            <MetricStat
              title={"Existing Accounts"}
              value={accountRecency.old_accounts}
            />
          </Box>
        </div>
      </Stack>
    </div>
  );
}

export default EventMetrics;
